"use client";
import clsx from 'clsx';
import Image from "next/image";
import { usePathname } from 'next/navigation';
import { useState, useId, useEffect, useRef, FormEvent, MouseEvent } from 'react';
import { avenirNextRegular, avenirNextDemi } from '@/styles/fonts';
import { handleFreeSignupFirstStep } from '@/app/login/actions';
import { logError } from '@/app/lib/logger';
import { lpHidesFreeModal } from '@/app/lp/landers';
import TurnstileWidget, { type TurnstileWidgetHandle } from '@/app/components/TurnstileWidget.client';

const OPEN_MODAL_EVENT = "openFreeModal";

// Call this function from anywhere to open the modal
export function openFreeModal() {
  window.dispatchEvent(new Event(OPEN_MODAL_EVENT));
}

function FreeModal({ isCaptchaEnabled }: { isCaptchaEnabled?: boolean }) {
  const pathname = usePathname();
  const [isVisible, setVisible] = useState(false);
  const [progress, setProgress] = useState("idle");
  const [errorEmail, setErrorEmail] = useState("");
  const [errorBusinessType, setErrorBusinessType] = useState("");
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);
  const idEmail = useId();
  const idBusinessType = useId();

  const resetTurnstile = () => {
    turnstileRef.current?.reset();
    setTurnstileToken('');
  };

  useEffect(() => {
    const handler = (e: { preventDefault: () => void; }) => {
      e.preventDefault();
      setVisible(true);
      setProgress("idle");
    };
    window.addEventListener(OPEN_MODAL_EVENT, handler);
    return () => window.removeEventListener(OPEN_MODAL_EVENT, handler);
  }, []);

  const handleBgClick = (event: MouseEvent<HTMLElement>) => {
    if (event.target === event.currentTarget) {
      event.preventDefault();
      setVisible(false);
      setProgress("idle");
    }
  };
  const closeDialog = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setVisible(false);
    setProgress("idle");
  };
  const onChangeEmail = () => setErrorEmail("");
  const onChangeBusinessType = () => setErrorBusinessType("");
  const handleSignup = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    setProgress("loading");

    if (isCaptchaEnabled && !turnstileToken) {
      setProgress("error");
      setErrorEmail("Please complete the captcha verification.");
      return;
    }

    if (turnstileToken) {
      formData.set('cf-turnstile-response', turnstileToken);
    }
    
    try {
      const result = await handleFreeSignupFirstStep(null, formData);
      
      if (result.errors && Object.keys(result.errors).length > 0) {
        setProgress("error");
        if (result.errors.email) {
          setErrorEmail(result.errors.email[0]);
        }
        if (result.errors.businessType) {
          setErrorBusinessType(result.errors.businessType[0]);
        }
        resetTurnstile();
      } else if (result.status === 'email-sent') {
        setProgress("success");
      } else {
        setProgress("internal_error");
        resetTurnstile();
      }
    } catch (error) {
      setProgress("internal_error");
      logError(error, { context: 'Signup error' });
      resetTurnstile();
    }
  };

  // Landing pages drive their own conversion flow; this modal would compete with it.
  if (lpHidesFreeModal(pathname ?? '')) {
    return null;
  }

  return (
    <>
      <div onClick={handleBgClick} className={clsx(
        !isVisible ? "hidden" :
          "z-50 fixed top-0 left-0 flex items-center justify-center w-full h-full min-h-screen px-4 py-5 bg-black/40 z-40 text-black",
      )} >
        <div className={clsx(avenirNextRegular.className,
        "md:flex w-full max-w-[750px] rounded-[5px] m-0 p-0 bg-[#222c3c] text-[24px] text-white")}>

          <div className="w-[100%] md:w-[40%] bg-[#2ba0f7] px-[40px] pt-[50px] pb-[20px] md:pb-[50px] rounded-l-[5px]">
            <Image src="/public/images/adbeat-logo.svg" alt="adbeat logo" width={94} height={23} />
            <div className="mt-[20px] md:mt-[45px] mb-[25px]">
              <h1 className="mb-[15px]">Get A Free Account</h1>
              <p className="text-[13px] opacity-60">Sign up for an Adbeat Basic account to see even more advertising data.</p>
            </div>
            <div className="mb-[25px]">
              <h1 className="text-[17px] mb-[15px]">What You&apos;ll Get</h1>
              <p className="text-[13px] opacity-60">
                Unlimited Views <br /> See more ad creatives<br /> Save your favorite ads
              </p>
            </div>
          </div>
          <div className="w-full md:w-[60%]">
            <div className="flex flex-col">
              <div className="py-[50px] px-[40px] grow">

                <form method="POST" onSubmit={handleSignup} id="signup_form">
                  <h1 className="mb-[25px]">
                    {
                      progress == "internal_error"
                        ? "Internal error. Please try again later."
                        : (progress == "success"
                          ? "We've sent you an email"
                          : "Create Your Free Account")
                    }
                  </h1>
                  <h4 className={clsx(
                    progress != "loading" ? "hidden" :
                      "text-[16px] text-[#86939e]")}>
                    One moment while we set up your free account
                  </h4>
                  <div className={clsx(
                    progress == "success" && "hidden",
                    avenirNextDemi.className, "text-[14px] mb-[20px]")}>
                    <label className="block mb-[5px]"
                      htmlFor={idEmail}>Email</label>
                    <input type="email" name="email" id={idEmail} placeholder="Your Email"
                      onChange={onChangeEmail}
                      className={clsx(
                        errorEmail == "" ? "border-[#313d4f]" : "border-[#a94442]",
                        "w-[100%] bg-[#273142] border py-[6px] pl-[12px] pr-[15px] rounded")} />
                    <div className="text-[#a94442] text-[11px] mt-[5px]">{errorEmail}</div>
                  </div>
                  <div className={clsx(
                    progress == "success" && "hidden",
                    avenirNextDemi.className, "text-[14px] mb-[20px]")}>
                    <label className="block mb-[5px]"
                      htmlFor={idBusinessType}>Business Type</label>
                    <select id={idBusinessType} name="business_type" defaultValue=""
                      onChange={onChangeBusinessType}
                      className={clsx(
                        errorBusinessType == "" ? "border-[#313d4f]" : "border-[#a94442]",
                        "w-[100%] bg-[#273142] border py-[6px] pl-[12px] pr-[15px] rounded")}>
                      <option value="">What&apos;s your Business Type?</option>
                      <option value="Advertiser">Advertiser</option>
                      <option value="Agency">Agency</option>
                      <option value="SEM Manager/Analyst">SEM Manager/Analyst</option>
                      <option value="Affiliate Marketer">Affiliate Marketer</option>
                      <option value="Publisher/Ad Network/DSP">Publisher/Ad Network/DSP</option>
                    </select>
                    <div className="text-[#a94442] text-[11px] mt-[5px]">{errorBusinessType}</div>
                  </div>
                  <input type="hidden" name="product" value="free" />
                  <input type="hidden" name="affId" value="" />
                  <input type="hidden" name="affCookie" value="" />
                  {isCaptchaEnabled && (
                    <div className="mb-[15px]">
                      <TurnstileWidget
                        ref={turnstileRef}
                        action="signup"
                        onTokenChange={setTurnstileToken}
                      />
                    </div>
                  )}
                  <button type="submit"
                    disabled={progress == "loading"}
                    className={clsx(
                      progress == "success" && "hidden",
                      avenirNextDemi.className,
                      "block w-[100%] py-[8px] px-[30px] rounded text-[14px]",
                      progress == "loading" 
                        ? "bg-[#1a7db8] opacity-60 cursor-not-allowed"
                        : "bg-[#2da1f8] hover:bg-[#1f8fe8] cursor-pointer"
                    )}>Sign Up</button>
                </form>
                <div className={clsx(
                  progress != "success" && "hidden",
                  ""
                )}>
                  <h4 className="text-[18px] text-[#86939e]">
                    Please check your Email Inbox and click on the link we&apos;ve sent to create your account.
                  </h4>
                  <p className="text-[14px] mt-[15px] mb-[30px]">(Don&apos;t forget to check your Spam or Promotions folder.)</p>

                  <button className={`${avenirNextDemi.className} block w-[100%] py-[8px] px-[30px] rounded text-[14px] bg-[#2da1f8]`} onClick={closeDialog}>
                    OK, I&apos;ll check my email!
                  </button>
                </div>
              </div>
              <div className="block text-center text-[14px] py-[20px] px-[15px] bg-[#273142] w-full rounded-br-[5px]">
                <p className="text-[#7f8fa4]">Already have an account?&nbsp;
                  <a className={`${avenirNextDemi.className} text-white focus:underline`} href="/login">Sign In</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export { FreeModal as default }